/* 
 
 Copyright© Celligence International, LLC - 2023
 
 This software program is protected by copyright laws and is the property of
 Celligence International, LLC (Celligence). Any unauthorized reproduction,
 distribution, modification, or public display of this program, without the
 explicit permission of Celligence is strictly prohibited. If you have gained
 access to this program without the expressed approval of Celligence report
 the incident to legal@celligence.com.
 
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UAParser } from "ua-parser-js";
import { Typography } from "@mui/material";
import { AngelSign, ERROR_MAP, Logo } from "morgan-sign";

//import Logo from "../Assets/Images/AngelSignLogo.png";
import Status from "../Components/Status";
import { LocationParser, OSParser,  BrowserParser} from "../Components/UtilityFunctions/Parsers";
import { UserWayAccessibilityMenu } from "../Components/UserWayAccessibilityMenu";

const WelcomePage = () => {

    const navigate = useNavigate();
    const { hashkey } = useParams();
    const [ipInfo, setIPInfo] = useState(null);
    const [timezone, setTimezone] = useState(null);
    const [user, setUser] = useState(null);

    const { os, browser } = UAParser(navigator.userAgent);
    const isLoading = "pending";

    // initial IP look up
    useEffect(() => {
        const fetchData = async () => {
            let error = null,
                datum = null,
                retry = 0;
            // We'll retry the network request 3 times or we receive our data
            // whichever comes first.
            // we can extend this to as many requests as needed pending network failures.
            while (retry < 3 && (datum === null || error !== null)) {

                // maxMind location look up
                try {
                    const requestData = {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json"
                        }
                    }

                    const maxMindRequest = await fetch(
                        process.env.REACT_APP_MAXMIND,
                        requestData
                    );
                    let userLocationData = await maxMindRequest.json();
                    // zipcode look up
                    let zipcodeData = null;
                    try {
                        if (userLocationData && userLocationData.hasOwnProperty("zipcode")) {
                            const zipcodeRequest = await fetch(
                                `${process.env.REACT_APP_RESTSERVER}/public/zipcode/${userLocationData.zipcode}`,
                                { method: "GET", headers: { "Content-Type": "application/json" } }
                            )
                            const jsonRes = await zipcodeRequest.json();
                            if (jsonRes && (!jsonRes.hasOwnProperty("error"))) {
                                zipcodeData = jsonRes;
                            } else
                                console.error("error in zipcode lookup.", jsonRes?.error);
                        }

                        const userInfo = {
                            ...userLocationData,
                            zipcodeData
                        }

                        error = null;
                        setIPInfo(userInfo);
                        return userInfo;
                    } catch (err) {
                        console.error({ message: "Failed to obtain zipcode info", error: err });
                        error = err;
                    }

                } catch (err) {
                    console.error({ message: "Failed to obtain location info", error: err });
                    error = err;
                }

                retry++;
            }
            // depending on management; we can redirect the user to error page if after 3 attempts, we haven't
            // the user's IP/Location.
            setIPInfo({ error: "Failed to obtain IP info" });
        };

        const TimeZoneGrabber = () => {
            const date = new Date();
            const dateAsString = date.toString();
            const timeZonePortion = dateAsString.split('(')[1];
            const timezone = timeZonePortion.slice(0, timeZonePortion.length - 1);
            return timezone;
        };
        setTimezone(TimeZoneGrabber());
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (ipInfo !== null) {
            setUser({
                userName: "",
                password: "",
                ipAddress: ipInfo && "userIp" in ipInfo ? ipInfo.userIp : "",
                location: ipInfo && "countryCode" in ipInfo ? LocationParser(ipInfo) : "",
                operatingSystem: OSParser(os),
                browser: BrowserParser(browser),
                timezone: timezone,
                hashKey: hashkey
            });
        }
    }, [ipInfo]);

    const onError = (errorCode, detail, data) => {
        let errorMessage = ERROR_MAP.get(errorCode);
        if (detail !== null && detail !== undefined && detail.length > 0)
            errorMessage = errorMessage + " " + detail;

        let iFrameCompletedDocument = {
            formName: data ? data.formName : "",
            loan: data ? data.loan : "",
            success: false,
            created: Date(Date.now())
        }
        window.parent.postMessage(iFrameCompletedDocument, "*");
        navigate("/error", { state: { data: errorMessage } });
    };

    const onSuccess = (status, data) => {
        if (!data) return;

        let iFrameCompletedDocument = {
            formName: data.formName,
            loan: data.loan,
            success: true,
            created: Date(Date.now())
        }
        window.parent.postMessage(iFrameCompletedDocument, "*");
        navigate("/finish", { state: { data } });
    };

    return (
        <>
            {user &&
                <div id="AngelSignWrapper" style={{ 
                    width: '100%',
                    height: '100dvh',
                    position: 'relative',
                    backgroundColor: 'gray', 
                    overflow: 'hidden' }}>
                    <AngelSign 
                        user={user} 
                        onSuccess={onSuccess} 
                        onError={onError}
                        sessionEndpointUrl={`${process.env.REACT_APP_RESTSERVER}/session`}
                        esignEndpointUrl={`${process.env.REACT_APP_RESTSERVER}/live/ai-sign/esignHandler`} 
                    />
                    <UserWayAccessibilityMenu color="#083f88" transparent={true} height={30} top={"95%"} left={"95%"} />
                </div>

            }
            {!user &&
                <div
                    className="WelcomePage"
                    style={{
                        backgroundColor: "#083f88",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                        height: "100vh",
                    }}
                >

                    <img src={Logo} alt="" style={{ marginTop: "1rem", maxWidth: "calc(100% - 2rem)", height: "auto", aspectRatio: "851/177" }} />
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            maxWidth: "500px",
                            margin: "1rem",
                            padding: "1rem 2rem",
                            height: "fit-content",
                            gap: "2rem",
                        }}
                    >
                        <Typography sx={{ fontSize: "1.5em", fontWeight: "bold", color: "#E9E9E9" }}>Loading...</Typography>
                        <Status isLoading={isLoading} />
                    </div>
                </div>
            }
        </>
    );
};

export default WelcomePage;

